import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  FormInstance,
  Input,
  InputNumber,
  message,
  Modal,
  notification,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { AxiosError } from 'axios';
import { cpf } from 'cpf-cnpj-validator';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { useQueryClient } from 'react-query';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import countries from 'world_countries_lists/data/countries/en/countries.json';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import InternationalPhoneInput from '../../components/ApplicantAnalysis/InternationalPhoneInput';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import TagInput from '../../components/TagInput';
import useCEPQuery from '../../hooks/useCEPQuery';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useSaveCandidateMutation from '../../hooks/useSaveCandidateMutation';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams, { Optional } from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import {
  CandidateDetail,
  CandidateDisabilityCollection,
  CandidateId,
  ExtendedCountryPhoneInputValue,
  JobPostingId,
  SerialID,
} from '../../types';
import {
  handlePossiblePhoneNumberValidation,
  handleRequiredPhoneValidation,
} from '../../validators/InternationalPhoneInputValidators';
import styles from './EditCandidateDetailsPage.module.css';

const KnowledgeModal: React.FC<{
  open: boolean;
  onClose: () => void | undefined;
  add: Function;
}> = ({ open, onClose, add }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  return (
    <Modal
      title={t('Adicionar conhecimento')}
      okText={t('Adicionar')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Helmet>
        <title>
          {t('Adicionar conhecimento')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form
        form={form}
        initialValues={{
          description: '',
        }}
        onFinish={({ description }) => {
          add({ id: undefined, description });
          onClose?.();
        }}
      >
        <Form.Item
          name="description"
          rules={[
            {
              required: true,
              message: t('Por favor, preencha um conhecimento.'),
              whitespace: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const AbilityModal: React.FC<{
  open: boolean;
  onClose: () => void | undefined;
  add: Function;
}> = ({ open, onClose, add }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  return (
    <Modal
      title={t('Adicionar habilidade')}
      okText={t('Adicionar')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Helmet>
        <title>
          {t('Adicionar habilidade')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form
        form={form}
        initialValues={{
          description: '',
        }}
        onFinish={({ description }) => {
          add({ id: undefined, description });
          onClose?.();
        }}
      >
        <Form.Item
          name="description"
          rules={[
            { required: true, message: t('Por favor, preencha uma habilidade.'), whitespace: true },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PersonalInterestModal: React.FC<{
  open: boolean;
  onClose: () => void | undefined;
  add: Function;
}> = ({ open, onClose, add }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  return (
    <Modal
      title={t('Adicionar interesse')}
      okText={t('Adicionar')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Helmet>
        <title>
          {t('Adicionar interesse')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form
        form={form}
        initialValues={{
          interest: '',
        }}
        onFinish={({ interest }) => {
          add({ id: undefined, interest });
          onClose?.();
        }}
      >
        <Form.Item
          name="interest"
          rules={[
            { required: true, message: t('Por favor, preencha um interesse.'), whitespace: true },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

interface EditApplicantFormValues {
  id: CandidateDetail['id'];
  name: string;
  email: string;
  phoneContacts?: ExtendedCountryPhoneInputValue[];
  birthday: string | null;
  candidateSource?: string;
  linkedInProfile?: string;
  salaryExpectation?: number;
  indicationEmail?: string;
  candidateDisabilitiesComplement?: {
    disability: CandidateDisabilityCollection['type'];
    cid?: string;
  }[];
  typeGender: NonNullable<CandidateDetail['gender']>;
  languages?: CandidateDetail['languages'];
  knowledges?: {
    id?: SerialID;
    description: string;
  }[];
  abilities?: {
    id?: SerialID;
    description: string;
  }[];
  personalInterests?: {
    id?: SerialID;
    interest: string;
  }[];
  cpf?: string;
  rg: string;
  address?: {
    postalCode?: string;
    additional?: string;
    number?: string;
    street?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  professionalExperiences?: {
    id?: SerialID;
    address?: string;
    companyName: string;
    description?: string;
    jobPositionName: string;
    startDate?: string;
    endDate?: string;
    currentlyWorks?: boolean;
  }[];
  education?: {
    id?: SerialID;
    courseName: string;
    conclusionYear?: string;
    institutionName: string;
    typeEducationLevel: string;
    typeSituation: string;
  }[];
  tags: number[];
}

interface CandidateSourceMutationFormValues {
  recruitmentProcessId?: SerialID;
  candidateSource?: string;
  indicationEmail?: string;
}

const EditCandidateDetailsPage = () => {
  const { t } = useTranslation();

  const { tenant, id, applicant_id } = useTypedParams({
    tenant: String,
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EditApplicantFormValues>();
  const professionalExperiencesValue = Form.useWatch('professionalExperiences', form);
  const [setIsDirty] = useDiscardChangesModal(form);
  const navigate = useTypedNavigate();
  const [searchParams] = useSearchParams();
  const [knowledgeModalVisible, setKnowledgeModalVisible] = useState(false);
  const [abilityModalVisible, setAbilityModalVisible] = useState(false);
  const [personalInterestModalVisible, setPersonalInterestModalVisible] = useState(false);
  const { setDrawerForm: setForm, setDrawerMutationIsLoading: setMutationIsLoading } =
    useOutletContext<{
      setDrawerForm: React.Dispatch<React.SetStateAction<FormInstance | undefined>>;
      setDrawerMutationIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    }>();
  const [cep, setCep] = useState<string>('');
  const countryValue = Form.useWatch(['address', 'country'], form);
  const stateValue = Form.useWatch(['address', 'state'], form);

  const states = useAPIQuery(
    `/country-state-city/countries/${countryValue?.toUpperCase()}/states/`,
    {
      enabled: !!countryValue,
      refetchOnWindowFocus: false,
    }
  );

  const cities = useAPIQuery(
    `/country-state-city/countries/${countryValue?.toUpperCase()}/states/${stateValue}/cities/`,
    {
      enabled: !!countryValue && !!stateValue,
      refetchOnWindowFocus: false,
    }
  );

  const address = useCEPQuery(cep, {
    enabled: !!cep,
    onSuccess: ({ neighborhood, city, state, street }) => {
      form.setFieldsValue({
        address: {
          street,
          neighborhood,
          city,
          state,
          country: 'BR',
        },
      });
    },
    onError: () => {
      setCep(''); // reseta o cep para permitir que a mensagem de erro seja exibida novamente
      message.error(t('CEP não encontrado.'));
    },
  });

  useEffect(() => {
    setForm(form);
  }, [form, setForm]);

  const candidateSourceOptionsPages = useInfiniteAPIQuery(['/candidate-source-options/']);

  const candidateSourceOptions = usePrefetchPages(
    candidateSourceOptionsPages,
    (data) => data.results
  );

  const mutation = useSaveCandidateMutation({
    onSettled: async () => {
      await queryClient.invalidateQueries('/candidates/');
    },
    onSuccess: () => {
      setIsDirty(false);
      message.success(t('Candidato atualizado!'));
      if (id) {
        navigate({
          pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
          params: { tenant, id, applicant_id },
          search: searchParams,
        });
      } else {
        navigate({
          pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
          params: { tenant, applicant_id },
          search: searchParams,
        });
      }
    },
    onError(error, variables, context) {
      if (
        (error as AxiosError).response?.data?.message ===
        t('E-mail já cadastrado para outro candidato.')
      ) {
        return notification.error({
          duration: 0,
          message: t('Erro'),
          description: t(
            'Já existe um candidato com esse endereço de e-mail. Por favor confira no Banco de Talentos.'
          ),
        });
      }

      return queryClient.defaultMutationOptions().onError?.(error, variables, context);
    },
  });

  const candidateSourceMutation = useAPIMutation(
    async (
      { recruitmentProcessId, candidateSource, indicationEmail }: CandidateSourceMutationFormValues,
      api
    ) => {
      await api.patch(`/recruitment-processes/${recruitmentProcessId}/`, {
        candidate_source_option: candidateSource,
        indication_email: indicationEmail,
      });
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/recruitment-processes/');
      },
    }
  );

  useEffect(() => {
    setMutationIsLoading(mutation.isLoading);
  }, [mutation.isLoading, setMutationIsLoading]);

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: applicant_id,
          job_posting: id,
        },
      },
    ],
    {
      enabled: !!id,
      select(data) {
        return data.count > 0 ? data.results[0] : null;
      },
    }
  );

  const candidateDisabilities = useAPIQuery([
    '/candidate-disability/',
    {
      params: {
        candidate: applicant_id,
      },
    },
  ]);

  const candidateTagsPages = useInfiniteAPIQuery('/candidate-tags/');
  const candidateTags = usePrefetchPages(candidateTagsPages, (data) => data.results);

  const validateBirthday = (_: unknown, value: moment.Moment | undefined) => {
    if (value && value.isAfter(moment(), 'day')) {
      return Promise.reject(
        new Error(t('A data de nascimento não pode ser posterior à data atual.'))
      );
    }
    return Promise.resolve();
  };

  const disabilitiesOptions = [
    {
      key: 'PHYSICAL',
      name: t('Física'),
      disabled: false,
    },
    {
      key: 'HEARING',
      name: t('Auditiva'),
      disabled: false,
    },
    {
      key: 'VISUAL',
      name: t('Visual'),
      disabled: false,
    },
    {
      key: 'MENTAL',
      name: t('Mental'),
      disabled: false,
    },
    {
      key: 'INTELLECTUAL',
      name: t('Intelectual'),
      disabled: false,
    },
    {
      key: 'OTHER',
      name: t('Outra'),
      disabled: false,
    },
  ];

  const selectedDisabilitiesRelation: Record<number, string> = {};

  let index = 0;

  for (const existingDisability of candidateDisabilities.data?.results || []) {
    const disability = disabilitiesOptions.find((option) => option.key === existingDisability.type);

    if (disability) {
      disability['disabled'] = true;
      selectedDisabilitiesRelation[index] = disability?.key;
      index++;
    }
  }

  return (
    <ErrorResult error={candidate.error}>
      <Spin spinning={candidate.isLoading}>
        <Helmet>
          <title>
            {t('Editar |')}
            {candidate.data?.name || '...'} | {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <Form<EditApplicantFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            ...(candidate.data || {}),
            typeGender: candidate.data?.gender,
            typeMaritalStatus: candidate.data?.marital_status,
            realocationPossibility: candidate.data?.realocation_possibility,
            travelPossibility: candidate.data?.travel_possibility,
            professionalGoal: candidate.data?.professional_goal,
            professionalSummary: candidate.data?.professional_summary,
            salaryExpectation: candidate.data?.salary_expectation,
            phoneContacts: candidate.data?.phone_contacts.map(
              ({ id, number, country_code, type }) => ({
                id,
                phoneNumber: {
                  phone: number,
                  code: countries.find((country) => country.id === country_code)?.id || 55,
                },
                phoneType: type,
              })
            ),
            candidateSource: recruitmentProcess.data?.candidate_source_option,
            linkedInProfile: candidate.data?.social_networks?.find(
              (socialNetwork) => socialNetwork.type === 'LINKEDIN'
            )?.profile,
            indicationEmail: recruitmentProcess.data?.indication_email,
            birthday: candidate.data?.birthdate ? moment(candidate.data?.birthdate) : null,
            languages: candidate.data?.languages,
            knowledges: candidate.data?.knowledge,
            abilities: candidate.data?.abilities,
            personalInterests: candidate.data?.personal_interests,
            education: candidate.data?.education.map(
              ({
                id,
                course_name,
                institution_name,
                start_date,
                end_date,
                conclusion_year,
                type_education_level,
                type_situation,
              }) => ({
                id,
                courseName: course_name,
                institutionName: institution_name,
                startDate: start_date ? moment(start_date) : undefined,
                endDate: end_date ? moment(end_date) : undefined,
                conclusionYear: conclusion_year,
                typeEducationLevel: type_education_level,
                typeSituation: type_situation,
              })
            ),
            address: {
              postalCode: candidate.data?.address_postal_code,
              additional: candidate.data?.address_additional,
              number: candidate.data?.address_number,
              street: candidate.data?.address_street,
              neighborhood: candidate.data?.address_administrative_region,
              city: candidate.data?.address_city,
              state: candidate.data?.address_state,
              country: candidate.data?.address_country,
            },
            professionalExperiences: candidate.data?.professional_experiences?.map(
              ({ id, start_date, end_date, description, company_name, job_position_name }) => ({
                id,
                startDate: start_date ? moment(start_date) : undefined,
                endDate: end_date ? moment(end_date) : undefined,
                currentlyWorks: end_date === null,
                jobPositionName: job_position_name,
                companyName: company_name,
                description,
              })
            ),
            portfolio: candidate.data?.portfolio,
            candidateDisabilitiesComplement: candidateDisabilities.data?.count
              ? candidateDisabilities.data.results.map(({ type, cid }) => ({
                  disability: type,
                  cid,
                }))
              : [],
            tags: candidate.data?.tags.map(({ id }) => id) || [],
          }}
          onFinish={(values) => {
            if (id && recruitmentProcess.data?.stage) {
              candidateSourceMutation.mutate({
                recruitmentProcessId: recruitmentProcess.data?.id,
                candidateSource: values.candidateSource,
                indicationEmail: values.indicationEmail,
              });
            }

            return mutation.mutate(values);
          }}
          onValuesChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Form.Item
            label={t('Nome')}
            name="name"
            rules={[
              {
                required: true,
                type: 'string',
                whitespace: true,
              },
              {
                validator: (rule, value: string) => {
                  if (value && value.split(' ').filter((v) => v).length < 2) {
                    return Promise.reject(
                      new Error(t('Por favor, insira o nome completo do candidato.'))
                    );
                  }

                  if (value && value.match(/[\d@]/g)) {
                    return Promise.reject(
                      new Error(t('Caracteres inválidos, por favor verifique a escrita.'))
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input data-testid="candidate-name-input" />
          </Form.Item>
          <Form.Row>
            <Form.Col span={12}>
              <Form.Item
                label={t('Email')}
                name="email"
                rules={[{ required: true, type: 'email' }]}
              >
                <Input placeholder={t('exemplo@email.com')} data-testid="candidate-email-input" />
              </Form.Item>
            </Form.Col>
            <Form.Col span={12}>
              <Form.Item
                label="LinkedIn"
                name="linkedInProfile"
                rules={[
                  {
                    pattern: /^https:\/\/www.linkedin.com\//,
                    message: t('O link deve começar com: https://www.linkedin.com/'),
                  },
                ]}
              >
                <Input
                  data-testid="candidate-linkedin-input"
                  placeholder={t('https://www.linkedin.com/in/exemplo')}
                />
              </Form.Item>
            </Form.Col>
          </Form.Row>
          <Form.Row>
            <Form.Col span={12}>
              <Form.Item
                label={t('Portfolio / Github / Site')}
                name="portfolio"
                rules={[{ whitespace: true }]}
              >
                <Input
                  data-testid="candidate-portfolio-input"
                  placeholder={t('https://www.exemplo.com')}
                />
              </Form.Item>
            </Form.Col>
            <Form.Col span={12}>
              <Form.Item label={t('Pretensão Salarial')} name="salaryExpectation">
                <InputNumber<number>
                  data-testid="candidate-salary-input"
                  controls={false}
                  addonBefore="R$"
                  formatter={(value) => `${value}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')}
                  parser={(value) => parseInt(value?.replace(/\$\s?|(\.*)/g, '') || '0', 10)}
                  min={0}
                  max={2147483647}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form.Col>
          </Form.Row>
          <Collapse
            ghost
            className={styles.collapse}
            expandIconPosition="end"
            defaultActiveKey={[
              'phoneContacts',
              'candidateSource',
              'professionalSummary',
              'professionalExperiences',
              'academicEducation',
              'languages',
              'knowledge',
              'abilities',
              'personalInterests',
              'address',
              'details',
              'tags',
            ]}
          >
            <Collapse.Panel
              key="tags"
              header={
                <Typography.Title level={3}>
                  <Trans>Tags</Trans>
                </Typography.Title>
              }
            >
              <Form.Item name="tags" label={t('Tags')}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={candidateTags.map((candidateTag) => ({
                    key: candidateTag.id,
                    label: candidateTag.name,
                    value: candidateTag.id,
                  }))}
                />
              </Form.Item>
            </Collapse.Panel>
            <Collapse.Panel
              key="phoneContacts"
              header={
                <Typography.Title level={3}>
                  <Trans>Contatos</Trans>
                </Typography.Title>
              }
            >
              <Form.List name="phoneContacts">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => {
                      return (
                        <Form.Row key={key}>
                          <Form.Col span={12}>
                            <Form.Item
                              label={t('Telefone')}
                              name={[name, 'phoneNumber']}
                              hasFeedback
                              validateFirst
                              validateTrigger="onChange"
                              rules={[
                                {
                                  validator: handlePossiblePhoneNumberValidation,
                                  message: t('Número de telefone inválido'),
                                },
                                {
                                  validator: handleRequiredPhoneValidation,
                                  message: t('Por favor, preencha o campo Telefone'),
                                },
                              ]}
                            >
                              <InternationalPhoneInput
                                maxLength={20}
                                data-testid="candidate-phone-contacts-input"
                              />
                            </Form.Item>
                          </Form.Col>
                          <Form.Col span={6}>
                            <Form.Item name={[name, 'phoneType']} label={<></>}>
                              <Select>
                                <Select.Option value="PERSONAL">
                                  <Trans>Pessoal</Trans>
                                </Select.Option>
                                <Select.Option value="RESIDENTIAL">
                                  <Trans>Residencial</Trans>
                                </Select.Option>
                                <Select.Option value="PROFESSIONAL">
                                  <Trans>Profissional</Trans>
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Form.Col>
                          <Form.Col>
                            <Form.Item label={<></>}>
                              <DeleteFilled onClick={() => remove(name)} />
                            </Form.Item>
                          </Form.Col>
                        </Form.Row>
                      );
                    })}
                    <Button
                      type="ghost"
                      onClick={() => add({ phoneNumber: { short: 'br' }, phoneType: 'PERSONAL' })}
                      block
                      icon={<PlusOutlined />}
                    >
                      {' '}
                      <Trans>Adicionar telefone</Trans>
                    </Button>
                  </>
                )}
              </Form.List>
            </Collapse.Panel>
            {id && recruitmentProcess.data?.stage && (
              <Collapse.Panel
                key="candidateSource"
                header={
                  <Typography.Title level={3}>
                    <Trans>Origem do candidato</Trans>
                  </Typography.Title>
                }
              >
                <Form.Item label={t('Como ficou sabendo da vaga?')} name="candidateSource">
                  <Select>
                    {candidateSourceOptions.map((sourceOption) => (
                      <Select.Option value={sourceOption.name} key={sourceOption.name}>
                        {sourceOption.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, next) => prev.candidateSource !== next.candidateSource}
                >
                  {(form) =>
                    form.getFieldValue('candidateSource') && (
                      <Form.Col span={10}>
                        <Form.Item
                          label={t('Email corporativo de quem indicou:')}
                          name="indicationEmail"
                          rules={[
                            { type: 'email', message: t('Email inválido') },
                            {
                              required: form.getFieldValue('candidateSource') === 'Indicação', // TODO: VER SE TRADUR AQ
                            },
                          ]}
                          hidden={form.getFieldValue('candidateSource') !== 'Indicação'} // TODO: VER SE TRADUR AQ
                        >
                          <Input
                            placeholder={t('exemplo@email.com')}
                            data-testid="candidate-indication-email-input"
                          />
                        </Form.Item>
                      </Form.Col>
                    )
                  }
                </Form.Item>
              </Collapse.Panel>
            )}

            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Resumo Profissional</Trans>
                </Typography.Title>
              }
              key="professionalSummary"
            >
              <Form.Item
                label={t('Objetivo')}
                name="professionalGoal"
                rules={[{ whitespace: true }]}
              >
                <Input.TextArea data-testid="candidate-professional-goal-input" />
              </Form.Item>
              <Form.Item
                label={t('Resumo')}
                name="professionalSummary"
                rules={[{ whitespace: true }]}
              >
                <Input.TextArea data-testid="candidate-professional-summary-input" />
              </Form.Item>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Experiências Profissionais</Trans>
                </Typography.Title>
              }
              key="professionalExperiences"
            >
              <Form.List name="professionalExperiences">
                {(fields, { add, remove }) => (
                  <Space direction="vertical">
                    {fields.map(({ key, name }) => (
                      <Card key={`professionalExperiences${key}`}>
                        <Form.Row justify="end">
                          <Button type="text" onClick={() => remove(name)}>
                            <DeleteFilled />
                          </Button>
                        </Form.Row>
                        <Form.Row>
                          <Form.Col span={12}>
                            <Form.Item
                              name={[name, 'jobPositionName']}
                              label={t('Cargo')}
                              rules={[{ required: true }]}
                            >
                              <Input />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'companyName']}
                              label={t('Empresa')}
                              rules={[{ required: true }]}
                            >
                              <Input />
                            </Form.Item>
                          </Form.Col>
                          <Form.Col span={12}>
                            <Form.Item
                              name={[name, 'startDate']}
                              label={t('Data de início')}
                              rules={[
                                {
                                  required: !!(
                                    professionalExperiencesValue?.[key]?.endDate ||
                                    professionalExperiencesValue?.[key]?.currentlyWorks
                                  ),
                                  message: t(
                                    'Por favor, preencha a data de início para completar o período de experiência'
                                  ),
                                },
                              ]}
                            >
                              <DatePicker
                                className={styles.datePicker}
                                disabledDate={(current) => {
                                  return current && current > moment().endOf('day');
                                }}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'endDate']}
                              label={t('Data de término')}
                              rules={[
                                {
                                  required:
                                    !!professionalExperiencesValue?.[key]?.startDate &&
                                    !(professionalExperiencesValue?.[key]?.currentlyWorks ?? false),
                                  message: t(
                                    'Por favor, preencha a data de término ou indique se trabalha atualmente para completar o período de experiência'
                                  ),
                                },
                                {
                                  validator(_, value: number | undefined) {
                                    if (
                                      professionalExperiencesValue?.[key]?.currentlyWorks ||
                                      !professionalExperiencesValue?.[key]?.startDate
                                    ) {
                                      return Promise.resolve();
                                    }

                                    if (
                                      moment(professionalExperiencesValue?.[key]?.startDate) >
                                      moment(value)
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          t(
                                            'O valor final da experiência profissional tem que ser maior ou igual do que o valor inicial.'
                                          )
                                        )
                                      );
                                    }

                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <DatePicker
                                data-testid="candidate-professional-experience-end-date"
                                disabled={
                                  professionalExperiencesValue?.[key]?.currentlyWorks ?? false
                                }
                                disabledDate={(current) => {
                                  return current && current > moment().endOf('day');
                                }}
                                className={styles.datePicker}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'currentlyWorks']}
                              valuePropName="checked"
                              noStyle
                            >
                              <Checkbox>
                                <Trans>Trabalha atualmente neste cargo</Trans>
                              </Checkbox>
                            </Form.Item>
                          </Form.Col>
                        </Form.Row>
                        <Form.Item
                          name={[name, 'description']}
                          rules={[{ whitespace: true }]}
                          label={t('Descrição')}
                        >
                          <Input.TextArea data-testid="candidate-professional-experiences-input" />
                        </Form.Item>
                      </Card>
                    ))}
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() => add({ currentlyWorks: false })}
                    >
                      {' '}
                      <Trans>Adicionar experiência profissional</Trans>
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Formações Acadêmicas</Trans>
                </Typography.Title>
              }
              key="academicEducation"
            >
              <Form.List name="education">
                {(fields, { add, remove }) => (
                  <Space direction="vertical">
                    {fields.map(({ key, name }) => (
                      <Card key={`education${key}`}>
                        <Form.Row justify="end">
                          <Button
                            type="text"
                            onClick={() => remove(name)}
                            data-testid="delete-education-input"
                          >
                            <DeleteFilled />
                          </Button>
                        </Form.Row>
                        <Form.Row>
                          <Form.Col span={12}>
                            <Form.Item
                              name={[name, 'courseName']}
                              label={t('Curso')}
                              rules={[{ required: true }]}
                            >
                              <Input data-testid="candidate-education-input" />
                            </Form.Item>
                          </Form.Col>
                          <Form.Col span={12}>
                            <Form.Item
                              name={[name, 'institutionName']}
                              label={t('Instituição')}
                              rules={[{ required: true }]}
                            >
                              <Input />
                            </Form.Item>
                          </Form.Col>
                        </Form.Row>
                        <Form.Row>
                          <Form.Col span={8}>
                            <Form.Item
                              name={[name, 'typeEducationLevel']}
                              label={t('Nível de formação')}
                              rules={[{ required: true }]}
                            >
                              <Select>
                                <Select.Option value="ELEMENTARY_SCHOOL_REGULAR">
                                  <Trans>Ensino fundamental</Trans>
                                </Select.Option>
                                <Select.Option value="HIGH_SCHOOL_REGULAR">
                                  <Trans>Ensino médio</Trans>
                                </Select.Option>
                                <Select.Option value="HIGHER_EDUCATION">
                                  <Trans>Superior</Trans>
                                </Select.Option>
                                <Select.Option value="POST_GRADUATE_SPECIALIZATION">
                                  <Trans>Especialização</Trans>
                                </Select.Option>
                                <Select.Option value="MASTERS_DEGREE">
                                  <Trans>Mestrado</Trans>
                                </Select.Option>
                                <Select.Option value="PHD_DEGREE">
                                  <Trans>Doutorado</Trans>
                                </Select.Option>
                                <Select.Option value="OTHER">
                                  <Trans>Outro</Trans>
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Form.Col>
                          <Form.Col span={8}>
                            <Form.Item
                              name={[name, 'typeSituation']}
                              label={t('Situação')}
                              rules={[{ required: true }]}
                            >
                              <Select>
                                <Select.Option value="IN_PROGRESS">
                                  <Trans>Em progresso</Trans>
                                </Select.Option>
                                <Select.Option value="FINALIZED">
                                  <Trans>Finalizado</Trans>
                                </Select.Option>
                                <Select.Option value="SUSPENDED">
                                  <Trans>Suspenso</Trans>
                                </Select.Option>
                                <Select.Option value="CANCELED">
                                  <Trans>Cancelado</Trans>
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Form.Col>
                          <Form.Col span={8}>
                            <Form.Item
                              label={t('Ano de conclusão (ou previsão)')}
                              name={[name, 'conclusionYear']}
                              rules={[
                                {
                                  min: 1000,
                                  max: 9999,
                                  type: 'number',
                                },
                              ]}
                            >
                              <InputNumber className={styles.inputNumber} min={1000} max={9999} />
                            </Form.Item>
                          </Form.Col>
                        </Form.Row>
                      </Card>
                    ))}
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>
                      <Trans>Adicionar formação acadêmica</Trans>
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Idiomas</Trans>
                </Typography.Title>
              }
              key="languages"
            >
              <Form.List name="languages" data-testid="candidate-language-input">
                {(fields, { add, remove }) => (
                  <Space direction="vertical">
                    {fields.map(({ key, name, ...restField }) => {
                      return (
                        <Space key={key} align="baseline">
                          <Form.Item
                            {...restField}
                            name={[name, 'language']}
                            rules={[
                              {
                                required: true,
                                message: t('Por favor, insira um idioma.'),
                                whitespace: true,
                              },
                            ]}
                          >
                            <Input
                              placeholder={t('Idioma')}
                              data-testid="candidate-language-input"
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'proficiency']}
                            className={styles.typeProficiency}
                            rules={[{ required: true, message: t('Por favor, insira um nível.') }]}
                          >
                            <Select placeholder={t('Nível')}>
                              <Select.Option value="BASIC">
                                <Trans>Básico</Trans>
                              </Select.Option>
                              <Select.Option value="INTERMEDIATE">
                                <Trans>Intermediário</Trans>
                              </Select.Option>
                              <Select.Option value="ADVANCED">
                                <Trans>Avançado</Trans>
                              </Select.Option>
                              <Select.Option value="FLUENT">
                                <Trans>Fluente</Trans>
                              </Select.Option>
                            </Select>
                          </Form.Item>
                          <DeleteFilled
                            onClick={() => remove(name)}
                            data-testid="delete-language-input"
                          />
                          <Form.Item {...restField} name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                        </Space>
                      );
                    })}
                    <Button
                      type="ghost"
                      onClick={() => add({ name: null, typeProficiency: null })}
                      block
                      icon={<PlusOutlined />}
                    >
                      <Trans>Adicionar idioma</Trans>
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Conhecimentos</Trans>
                </Typography.Title>
              }
              key="knowledge"
            >
              <Form.List name="knowledges">
                {(fields, { add, remove }) => (
                  <>
                    <KnowledgeModal
                      open={knowledgeModalVisible}
                      onClose={() => setKnowledgeModalVisible(false)}
                      add={add}
                    />
                    <Form.Item>
                      {fields.map(({ key, name, ...restField }) => (
                        <span key={key} data-testid="candidate-knowledges-input">
                          <Form.Item {...restField} name={[name, 'description']} noStyle>
                            <TagInput
                              closable
                              onClose={() => remove(name)}
                              data-testid="delete-knowledges-input"
                            />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                        </span>
                      ))}
                    </Form.Item>
                    <Button
                      type="ghost"
                      onClick={() => setKnowledgeModalVisible(true)}
                      block
                      icon={<PlusOutlined />}
                    >
                      <Trans>Adicionar conhecimento</Trans>
                    </Button>
                  </>
                )}
              </Form.List>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Habilidades</Trans>
                </Typography.Title>
              }
              key="abilities"
            >
              <Form.List name="abilities">
                {(fields, { add, remove }) => (
                  <>
                    <AbilityModal
                      open={abilityModalVisible}
                      onClose={() => setAbilityModalVisible(false)}
                      add={add}
                    />
                    <Form.Item>
                      {fields.map(({ key, name, ...restField }) => (
                        <span key={key} data-testid="candidate-abilities-input">
                          <Form.Item {...restField} name={[name, 'description']} noStyle>
                            <TagInput closable onClose={() => remove(name)} />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                        </span>
                      ))}
                    </Form.Item>
                    <Button
                      type="ghost"
                      onClick={() => setAbilityModalVisible(true)}
                      block
                      icon={<PlusOutlined />}
                    >
                      <Trans>Adicionar habilidade</Trans>
                    </Button>
                  </>
                )}
              </Form.List>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Interesses Pessoais</Trans>
                </Typography.Title>
              }
              key="personalInterests"
            >
              <Form.List name="personalInterests">
                {(fields, { add, remove }) => (
                  <>
                    <PersonalInterestModal
                      open={personalInterestModalVisible}
                      onClose={() => setPersonalInterestModalVisible(false)}
                      add={add}
                    />
                    <Form.Item>
                      {fields.map(({ key, name, ...restField }) => (
                        <span key={key} data-testid="candidate-personal-interests-input">
                          <Form.Item {...restField} name={[name, 'interest']} noStyle>
                            <TagInput closable onClose={() => remove(name)} />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                        </span>
                      ))}
                    </Form.Item>
                    <Button
                      type="ghost"
                      onClick={() => setPersonalInterestModalVisible(true)}
                      block
                      icon={<PlusOutlined />}
                    >
                      <Trans>Adicionar interesse</Trans>
                    </Button>
                  </>
                )}
              </Form.List>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Endereço</Trans>
                </Typography.Title>
              }
              key="address"
            >
              <Form.Row>
                <Form.Col span={6}>
                  <Form.Item
                    name={['address', 'postalCode']}
                    label={t('CEP')}
                    hasFeedback={address.isLoading}
                    validateStatus="validating"
                  >
                    <NumberFormat
                      data-testid="candidate-cep-input"
                      onSearch={(value?: string) => {
                        const cepNumber = value?.replace('-', '').trim();
                        if (cepNumber?.length === 8) {
                          setCep(cepNumber);
                        } else {
                          message.error(t('CEP inválido para busca.'));
                        }
                      }}
                      format="#####-###"
                      customInput={Input.Search}
                    />
                  </Form.Item>
                </Form.Col>
                <Form.Col span={12}>
                  <Form.Item name={['address', 'street']} label={t('Rua')}>
                    <Input disabled={address.isLoading} data-testid="candidate-street-input" />
                  </Form.Item>
                </Form.Col>
                <Form.Col span={6}>
                  <Form.Item name={['address', 'number']} label={t('Número')}>
                    <Input data-testid="candidate-number-input" />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Form.Row>
                <Form.Col span={6}>
                  <Form.Item name={['address', 'additional']} label={t('Complemento')}>
                    <Input data-testid="candidate-additional-input" />
                  </Form.Item>
                </Form.Col>
                <Form.Col span={6}>
                  <Form.Item label={t('País')} name={['address', 'country']}>
                    <Select
                      allowClear
                      showSearch
                      data-testid="candidate-country-input"
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onClear={() => {
                        form.setFieldValue(['address', 'state'], undefined);
                        form.setFieldValue(['address', 'city'], undefined);
                      }}
                      onChange={(value) => {
                        form.setFieldValue(['address', 'country'], value);
                        form.setFieldValue(['address', 'state'], undefined);
                        form.setFieldValue(['address', 'city'], undefined);
                      }}
                    >
                      <Select.Option key={76} value="BR">
                        <Trans>Brasil</Trans>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Form.Col>
                <Form.Col span={6}>
                  <Form.Item name={['address', 'state']} label={t('Estado')}>
                    <Select
                      allowClear
                      showSearch
                      data-testid="candidate-state-input"
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      disabled={address.isLoading}
                      onClear={() => form.setFieldValue(['address', 'city'], undefined)}
                      onChange={(value) => {
                        form.setFieldValue(['address', 'state'], value);
                        form.setFieldValue(['address', 'city'], undefined);
                      }}
                    >
                      {states.data?.map((state) => (
                        <Select.Option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Form.Col>
                <Form.Col span={6}>
                  <Form.Item label={t('Cidade')} name={['address', 'city']}>
                    <Select
                      allowClear
                      showSearch
                      disabled={address.isLoading}
                      data-testid="candidate-city-input"
                    >
                      {cities.data?.map((city) => (
                        <Select.Option key={city.name} value={city.name}>
                          {city.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Form.Row>
                <Form.Col span={6}>
                  <Form.Item name={['address', 'neighborhood']} label={t('Bairro')}>
                    <Input
                      disabled={address.isLoading}
                      data-testid="candidate-neighborhood-input"
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Form.Row>
                <Form.Col span={12}>
                  <Form.Item name="realocationPossibility" label={t('Possibilidade de mudança?')}>
                    <Radio.Group>
                      <Radio value data-testid="candidate-relocation-possibility-input-yes">
                        <Trans>Sim</Trans>
                      </Radio>
                      <Radio value={false} data-testid="candidate-relocation-possibility-input-no">
                        <Trans>Não</Trans>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Form.Col>
                <Form.Col span={12}>
                  <Form.Item name="travelPossibility" label={t('Aceita viajar pela empresa?')}>
                    <Radio.Group>
                      <Radio value data-testid="candidate-travel-possibility-input-yes">
                        <Trans>Sim</Trans>
                      </Radio>
                      <Radio value={false} data-testid="candidate-travel-possibility-input-no">
                        <Trans>Não</Trans>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Form.Col>
              </Form.Row>
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <Typography.Title level={3}>
                  <Trans>Detalhes</Trans>
                </Typography.Title>
              }
              key="details"
            >
              <Form.Row>
                <Form.Col span={8}>
                  <Form.Item name="typeGender" label={t('Gênero')}>
                    <Select data-testid="candidate-type-gender-input">
                      <Select.Option value="MALE">
                        <Trans>Masculino</Trans>
                      </Select.Option>
                      <Select.Option value="FEMALE">
                        <Trans>Feminino</Trans>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Form.Col>
                <Form.Col span={8}>
                  <Form.Item name="typeMaritalStatus" label={t('Estado Civil')}>
                    <Select>
                      <Select.Option value="MARRIED">
                        <Trans>Casado</Trans>
                      </Select.Option>
                      <Select.Option value="SEPARATED">
                        <Trans>Separado</Trans>
                      </Select.Option>
                      <Select.Option value="DIVORCED">
                        <Trans>Divorciado</Trans>
                      </Select.Option>
                      <Select.Option value="SINGLE">
                        <Trans>Solteiro</Trans>
                      </Select.Option>
                      <Select.Option value="STABLE_UNION">
                        <Trans>União estável</Trans>
                      </Select.Option>
                      <Select.Option value="WIDOW">
                        <Trans>Viúvo</Trans>
                      </Select.Option>
                      <Select.Option value="CONCUBINAGE">
                        <Trans>Concubinato</Trans>
                      </Select.Option>
                      <Select.Option value="OTHER">
                        <Trans>Outro</Trans>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Form.Col>
                <Form.Col span={8}>
                  <Form.Item name="rg" label={t('RG')}>
                    <Input data-testid="candidate-rg-input" />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Form.Row>
                <Form.Col span={8}>
                  <Form.Item
                    name="cpf"
                    label={t('CPF')}
                    rules={[
                      {
                        validator(_, value: string | undefined) {
                          if (!value || value === '' || cpf.isValid(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('Insira um CPF válido')));
                        },
                      },
                    ]}
                  >
                    <NumberFormat
                      format="###.###.###-##"
                      customInput={Input}
                      data-testid="candidate-cpf-input"
                    />
                  </Form.Item>
                </Form.Col>
                <Form.Col span={8}>
                  <Form.Item
                    name="birthday"
                    label={t('Data de Nascimento')}
                    rules={[{ validator: validateBirthday }]}
                  >
                    <DatePicker
                      data-testid="candidate-birthday-input"
                      format="DD/MM/YYYY"
                      className={styles.datePicker}
                      disabledDate={(current) => {
                        return current && current > moment().subtract(14, 'years').endOf('day');
                      }}
                      defaultPickerValue={moment().subtract(14, 'years').endOf('day')} //FIXME: esse propriedade será deprecada, mas é a única que coloca a data só dentro do componente quando não tem valor definido, o defaultValue já coloca dentro do form, não é o que queremos
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Form.Row>
                <Form.Col span={24}>
                  <Form.Item
                    name="disabilitiesContainer"
                    hasFeedback
                    validateFirst
                    validateTrigger="onChange"
                  >
                    <Form.List name="candidateDisabilitiesComplement">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name }) => {
                            return (
                              <Form.Row key={key} gutter={[16, 16]}>
                                <Form.Col span={11}>
                                  <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, next) =>
                                      prev.candidateDisabilitiesComplement !==
                                      next.candidateDisabilitiesComplement
                                    }
                                  >
                                    {(form) => (
                                      <Form.Item
                                        label={t('Especifique a deficiência')}
                                        name={[name, 'disability']}
                                        required
                                        rules={[
                                          {
                                            required: true,
                                            message: t('Por favor, selecione uma deficiência'),
                                          },
                                        ]}
                                      >
                                        <Select
                                          allowClear
                                          data-testid={`select-disability-input-${name}`}
                                          onChange={(value) => {
                                            if (!value) {
                                              const deselectedValue =
                                                selectedDisabilitiesRelation[name];

                                              const disability = disabilitiesOptions.find(
                                                (option) => option.key === deselectedValue
                                              );
                                              if (disability) {
                                                disability['disabled'] = false;
                                              }

                                              delete selectedDisabilitiesRelation[name];
                                            } else {
                                              const disability = disabilitiesOptions.find(
                                                (option) => option.key === value
                                              );
                                              if (disability) {
                                                disability['disabled'] = true;
                                              }

                                              selectedDisabilitiesRelation[name] = value;
                                            }
                                          }}
                                        >
                                          {disabilitiesOptions.map((disability) => (
                                            <Select.Option
                                              value={disability.key}
                                              key={disability.key}
                                              disabled={disability.disabled}
                                            >
                                              {disability.name}
                                            </Select.Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    )}
                                  </Form.Item>
                                </Form.Col>
                                <Form.Col span={11}>
                                  <Form.Item label={t('Insira o CID')} name={[name, 'cid']}>
                                    <Input data-testid={`cid-input-${name}`} maxLength={30} />
                                  </Form.Item>
                                </Form.Col>
                                <Form.Col span={2}>
                                  <Form.Item label={<></>}>
                                    <DeleteFilled
                                      onClick={() => {
                                        const deselectedValue = selectedDisabilitiesRelation[name];

                                        const disability = disabilitiesOptions.find(
                                          (option) => option.key === deselectedValue
                                        );
                                        if (disability) {
                                          disability['disabled'] = false;
                                        }

                                        delete selectedDisabilitiesRelation[name];

                                        remove(name);
                                      }}
                                    />
                                  </Form.Item>
                                </Form.Col>
                              </Form.Row>
                            );
                          })}
                          <Form.Col span={24}>
                            <Button
                              type="ghost"
                              onClick={() => add()}
                              block
                              icon={<PlusOutlined />}
                            >
                              <Trans>Adicionar deficiência</Trans>
                            </Button>
                          </Form.Col>
                        </>
                      )}
                    </Form.List>
                  </Form.Item>
                </Form.Col>
              </Form.Row>
            </Collapse.Panel>
          </Collapse>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(EditCandidateDetailsPage);
